import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useStock } from '../../context/StockContext';
import { useStockAuth } from '../../context/StockAuthContext';

const B = '#0B3D6E', T = '#0F6E56';

function KPI({ label, value, bg, color, icon }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'1.2rem', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ width:38, height:38, borderRadius:10, background:bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, marginBottom:10 }}>{icon}</div>
      <div style={{ fontSize:26, fontWeight:600, color: color||'#1a2535', marginBottom:2 }}>{value}</div>
      <div style={{ fontSize:12, color:'#64748b' }}>{label}</div>
    </div>
  );
}

export default function StockDashboard() {
  const { stats, loading, fetchAll, markAlertsRead, alerts } = useStock();
  const { user } = useStockAuth();

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const unread = alerts.filter(a=>!a.isRead).length;

  if (loading && !stats) return (
    <div style={{ padding:'4rem', textAlign:'center', color:'#94a3b8' }}>
      <div style={{ fontSize:40, marginBottom:12 }}>⏳</div>
      Chargement du tableau de bord...
    </div>
  );

  return (
    <div style={{ maxWidth:1000, margin:'0 auto', padding:'1.5rem' }}>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.5rem', flexWrap:'wrap', gap:10 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:600, marginBottom:4 }}>
            Bonjour, {user?.firstName || user?.username} 👋
          </h1>
          <div style={{ fontSize:13, color:'#64748b' }}>
            {user?.company} — {new Date().toLocaleDateString('fr-FR',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
          </div>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <Link to="/stock/products" style={{ background:B, color:'#fff', padding:'9px 16px', borderRadius:8, fontSize:13, fontWeight:600, textDecoration:'none' }}>
            + Ajouter produit
          </Link>
          {user?.plan === 'free' && (
            <Link to="/stock/upgrade" style={{ background:'linear-gradient(135deg,#f59e0b,#d97706)', color:'#fff', padding:'9px 16px', borderRadius:8, fontSize:13, fontWeight:600, textDecoration:'none' }}>
              ⭐ Passer Pro
            </Link>
          )}
        </div>
      </div>

      {/* Plan banner free */}
      {user?.plan === 'free' && stats && (
        <div style={{ background:'#fef3c7', border:'1px solid #fcd34d', borderRadius:10, padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.5rem', flexWrap:'wrap', gap:10 }}>
          <div>
            <div style={{ fontSize:13, color:'#92400e', fontWeight:500, marginBottom:6 }}>
              Plan Gratuit — {stats.totalProducts}/{stats.maxProducts} produits utilisés
            </div>
            <div style={{ width:220, height:6, background:'#fde68a', borderRadius:3, overflow:'hidden' }}>
              <div style={{ width:`${Math.min(stats.planUsage||0,100)}%`, height:6, background:stats.planUsage>=90?'#dc2626':'#d97706', borderRadius:3, transition:'width 0.3s' }}/>
            </div>
          </div>
          <Link to="/stock/upgrade" style={{ background:'#d97706', color:'#fff', padding:'8px 16px', borderRadius:8, fontSize:12, fontWeight:600, textDecoration:'none', flexShrink:0 }}>
            Passer au Pro →
          </Link>
        </div>
      )}

      {/* Alerts banner */}
      {unread > 0 && (
        <div style={{ background:'#fee2e2', border:'1px solid #fca5a5', borderRadius:10, padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.5rem', flexWrap:'wrap', gap:8 }}>
          <div style={{ fontSize:13, color:'#991b1b' }}>
            🔔 <strong>{unread} alerte(s) non lue(s)</strong> — stock faible ou produits expirant bientôt
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <Link to="/stock/alerts" style={{ color:'#dc2626', fontSize:12, fontWeight:600, textDecoration:'none' }}>Voir les alertes →</Link>
            <button onClick={()=>markAlertsRead('all')} style={{ background:'none', border:'none', color:'#94a3b8', fontSize:12, cursor:'pointer' }}>Tout marquer lu</button>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:'1.5rem' }}>
        <KPI label="Total produits"    value={stats?.totalProducts||0}  icon="📦" bg="#E6F1FB" color={B}/>
        <KPI label="Stock faible"      value={stats?.lowStockCount||0}  icon="⚠️" bg="#fee2e2" color="#dc2626"/>
        <KPI label="Expirant bientôt"  value={stats?.expiringCount||0}  icon="📅" bg="#fef3c7" color="#d97706"/>
        <KPI label="Valeur stock"      value={`${(stats?.totalValue||0).toLocaleString()} MAD`} icon="💰" bg="#E1F5EE" color={T}/>
        <KPI label="Alertes actives"   value={stats?.unreadAlerts||0}   icon="🔔" bg="#f3f0ff" color="#7C3AED"/>
      </div>

      {/* Two columns */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:16 }}>

        {/* Low stock */}
        <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ padding:'1rem 1.2rem', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <h3 style={{ fontSize:14, fontWeight:600 }}>⚠️ Stock faible</h3>
            <Link to="/stock/products" style={{ fontSize:12, color:B, textDecoration:'none' }}>Voir tout →</Link>
          </div>
          <div style={{ padding:'0.5rem' }}>
            {(stats?.lowStockProducts||[]).length === 0 ? (
              <div style={{ padding:'1.5rem', textAlign:'center', color:'#94a3b8', fontSize:13 }}>✅ Aucun stock faible</div>
            ) : (stats?.lowStockProducts||[]).map(p => (
              <div key={p._id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 10px', borderRadius:8, marginBottom:4, background:'#fff7f7' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:500 }}>{p.name}</div>
                  <div style={{ fontSize:11, color:'#94a3b8' }}>Seuil min : {p.minQuantity} {p.unit}</div>
                </div>
                <span style={{ background:'#fee2e2', color:'#dc2626', fontSize:12, fontWeight:700, padding:'3px 9px', borderRadius:8 }}>
                  {p.quantity} {p.unit}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent movements */}
        <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ padding:'1rem 1.2rem', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <h3 style={{ fontSize:14, fontWeight:600 }}>📋 Mouvements récents</h3>
            <Link to="/stock/movements" style={{ fontSize:12, color:B, textDecoration:'none' }}>Voir tout →</Link>
          </div>
          <div style={{ padding:'0.5rem' }}>
            {(stats?.recentMovements||[]).length === 0 ? (
              <div style={{ padding:'1.5rem', textAlign:'center', color:'#94a3b8', fontSize:13 }}>Aucun mouvement récent</div>
            ) : (stats?.recentMovements||[]).slice(0,8).map(m => (
              <div key={m._id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 10px', borderRadius:8, marginBottom:3 }}
                onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <div>
                  <div style={{ fontSize:12, fontWeight:500 }}>{m.productName}</div>
                  <div style={{ fontSize:11, color:'#94a3b8' }}>{m.reason||'—'} · {new Date(m.createdAt).toLocaleDateString('fr-FR')}</div>
                </div>
                <span style={{ fontSize:13, fontWeight:700, color: m.type==='in'?T:m.type==='out'?'#dc2626':'#7C3AED' }}>
                  {m.type==='in'?'+':m.type==='out'?'-':'~'}{m.quantity}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

